/* GENERATED from src/js/ndog-testimonials.js — edit the source, then run `make js`. Never edit this file by hand. */
/*
  ndog-testimonials.js
  Self-contained web component for the NDOG Testimonials slider.
  Page-based horizontal scroll-snap slider (a "page" = the number of cards
  visible at once, so 3 on desktop, 1 on mobile). Dots reflect pages, arrows
  move one page, the last page snaps to the end. Non-looping: prev hides at the
  start, next hides at the end.
  Optional keyword filter: chips rendered by Liquid ([data-filters]) toggle
  cards via their data-keywords attribute; the slider re-measures after every
  filter change. All geometry works on the currently VISIBLE cards, so arrows
  and dots stay correct when a filter shrinks the set. No dependencies.
  Optional star filter (block 'rating_header'): histogram rows
  ([data-rating-filter]) show only cards whose data-rating matches — mutually
  exclusive with the keyword filter, each activation resets the other. The
  chip ([data-rating-chip]) clears it. On activation the visible matches
  are topped up to the configured initial count (jm_max_reviews_* per
  viewport) by auto-fetching server-filtered pages from the widget
  endpoint (filter_rating param, own pagination per rating). While that
  fetch runs EVERYTHING stays hidden (loaded matches and the load-more CTA
  alike) and [data-rating-empty] doubles as the loading indicator; the
  confirmed set then appears in one simultaneous batch (revealRatingCards).
  Only when the fetch yields nothing at all does the final empty note stay.
  Optional category summaries ([data-summary], block type 'summary'):
  regular cards at the START of the track, one per filter keyword — only
  visible while their keyword filter is active ("All" shows none), so an
  active filter leads with its summary as the first card.
  Optional Judge.me load-more: a CTA card ([data-jm-cta]) fetches further
  review pages from Judge.me's public widget endpoint on click and appends
  them as cards (cloned from the Liquid [data-jm-card-template]). Any
  endpoint failure just removes the CTA card — the slider keeps working.
  Layout modes (data-layout, section setting): 'slider' (default) or 'grid'
  (Pinterest-style masonry). In grid mode all slider geometry (arrows, dots,
  scroll/resize tracking) is skipped — Liquid does not even render those
  elements — while filters, read-more, photo thumbs and Judge.me load-more
  keep working unchanged. The server ships a pure-CSS multi-column fallback;
  this component upgrades it to real column containers (.is-cols) so that
  already-visible cards KEEP their position when more reviews are loaded
  (CSS columns would re-balance everything), and filter changes glide the
  container height instead of collapsing in one frame. Cards are dealt into
  the currently shortest column (true masonry packing), so single very long
  reviews don't unbalance the columns.
*/
if (!customElements.get('ndog-testimonials')) {
  customElements.define(
    'ndog-testimonials',
    class NdogTestimonials extends HTMLElement {
      // Cache elements, wire events, run the first measurement. Bail out if empty.
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.allCards = Array.from(this.querySelectorAll('.ndog-testimonial-card'));
        this.dotsContainer = this.querySelector('[data-dots]');
        this.prevBtn = this.querySelector('[data-dir="prev"]');
        this.nextBtn = this.querySelector('[data-dir="next"]');
        this.filterButtons = Array.from(this.querySelectorAll('[data-filters] [data-filter]'));
        this.ratingButtons = Array.from(this.querySelectorAll('[data-rating-filter]'));
        this.ratingChip = this.querySelector('[data-rating-chip]');
        this.ratingHint = this.querySelector('[data-rating-hint]');
        this.emptyMessage = this.querySelector('[data-rating-empty]');
        if (!this.track || this.allCards.length === 0) return;

        // Grid mode (Pinterest masonry) is pure CSS — no slider geometry needed.
        this.isGrid = this.dataset.layout === 'grid';

        // Judge.me load-more state (all optional — attributes only exist
        // when the section settings enable the feature).
        this.currentFilter = '';
        this.currentRating = 0;
        this.jmFilterMode = this.dataset.jmFilterMode || 'all_only';
        this.jmMaxWords = parseInt(this.dataset.jmMaxWords, 10) || 0;
        this.jmShowPhotos = 'jmShowPhotos' in this.dataset;
        this.jmTemplate = this.querySelector('[data-jm-card-template]');
        this.jmCta = this.querySelector('[data-jm-cta]');
        this.jmUuids = new Set(this.allCards.map((card) => card.dataset.uuid).filter(Boolean));
        // Fetched/total pages per scope: key 0 = unfiltered, 1–5 = one star
        // rating (server-filtered via filter_rating) — separate paginations.
        this.jmPages = {};
        this.jmTotals = {};
        // Mobile cap for cards appended per load-more click (section setting);
        // reviews fetched beyond the cap wait in a per-scope queue.
        this.jmMobileBatch = parseInt(this.dataset.jmMobileBatch, 10) || 0;
        this.jmQueues = {};
        this.jmBusy = false;
        // True only while a star-filter top-up fetch is in flight — hides
        // the load-more CTA for that moment (jmBusy alone can't: it is
        // also true during load-more clicks, where the CTA must stay
        // visible to show its loading label).
        this.jmTopUp = false;
        // Per-viewport caps on the initially VISIBLE server-rendered cards
        // (section settings; 0 = no cap, mobile 0 falls back to the desktop
        // cap). Capped cards wait hidden in jmDeferred (card → position);
        // the load-more CTA reveals them before any fetch.
        // Live media query, NOT a one-time check: the Customizer's device
        // toggle (and rotation) resizes the viewport without reloading.
        this.mobileMq = window.matchMedia('(max-width: 749px)');
        this.jmDesktopInitial = parseInt(this.dataset.jmDesktopInitial, 10) || 0;
        this.jmMobileInitial = parseInt(this.dataset.jmMobileInitial, 10) || 0;
        this.jmDeferred = new Map();
        this.deferInitialJmCards();
        this.onMobileMqChange = () => this.applyDeferredVisibility();
        this.mobileMq.addEventListener('change', this.onMobileMqChange);

        this.bindEvents();
        this.setupWriteReview();
        if (this.isGrid) {
          this.setupGrid();
        } else {
          this.buildDots();
          this.update();
        }
        this.topUpInitialCards();
      }

      // The server markup only holds Judge.me's first widget page (5 reviews,
      // the metafield's page size) — on load, fetch further pages until the
      // default view reaches the current viewport's initial cap.
      async topUpInitialCards() {
        if (!this.jmTemplate || this.jmBusy || this.jmExhausted(0)) return;
        const cap = this.activeInitialCap();
        if (cap === Infinity) return;
        const reviewCards = this.allCards.filter(
          (card) => !('summary' in card.dataset) && !('jmCta' in card.dataset)
        ).length;
        const needed = cap - reviewCards;
        if (needed <= 0) return;
        this.jmBusy = true;
        try {
          const appended = await this.fetchUntilAppended(0, needed);
          if (appended > 0 && !this.isGrid) {
            this.buildDots();
            this.update();
          }
          if (this.jmExhausted(0) && !this.jmDeferredPending()) this.removeJmCta();
        } catch (error) {
          // Endpoint unreachable — the server-rendered cards stay, the
          // load-more CTA remains for a manual retry.
        } finally {
          this.jmBusy = false;
        }
      }

      // Remove listeners so nothing leaks.
      disconnectedCallback() {
        if (this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        if (this.onMobileMqChange) this.mobileMq.removeEventListener('change', this.onMobileMqChange);
        if (this.gridMq) this.gridMq.removeEventListener('change', this.onGridMqChange);
        if (this.gridMqTablet) this.gridMqTablet.removeEventListener('change', this.onGridMqChange);
        clearTimeout(this.writeReviewTimer);
      }

      /* ---- "Write a review" → native Judge.me form ---- */

      // Our write-review button (filter row or rating header — only one is
      // ever rendered) forwards to the write-review trigger of the hidden
      // native Judge.me widget ([data-jm-widget-host]) so the FULL Judge.me
      // flow (multi-step popup, uploads, custom questions — all backend
      // settings) runs unchanged. The engine is bootstrapped by the Judge.me
      // app embed; until it has rendered a live trigger the button stays
      // hidden, so a disabled embed never leaves a dead click.
      setupWriteReview() {
        const button = this.querySelector('[data-write-review-open]');
        if (!button) return;
        button.hidden = true;
        let tries = 0;
        const probe = () => {
          const trigger = this.jmWriteTrigger();
          if (trigger) {
            button.hidden = false;
            return;
          }
          tries += 1;
          // ~15s of patience for slow connections, then give up silently.
          if (tries < 30) this.writeReviewTimer = setTimeout(probe, 500);
        };
        probe();
        button.addEventListener('click', () => {
          const trigger = this.jmWriteTrigger();
          if (trigger) trigger.click();
        });
      }

      // Judge.me's own write-review trigger, covering both engine
      // generations: the revamp (Vue) widget renders a plain button whose
      // label is the merchant's backend text (widget_open_form_text) — we
      // match on that, since its internal classes are not stable API. The
      // classic engine keeps the .jdgm-write-rev-link, which only becomes
      // live once the engine has marked the widget as set up (before that,
      // and in the hidden legacy copy on the revamp path, it is inert).
      jmWriteTrigger() {
        if (typeof window.jdgmSettings === 'undefined') return null;
        const host = this.querySelector('[data-jm-widget-host]');
        if (!host) return null;
        const label = (window.jdgmSettings.widget_open_form_text || '').trim();
        if (label) {
          const byLabel = Array.from(host.querySelectorAll('button, a')).find(
            (el) => !el.closest('.jdgm-legacy-widget-content') && el.textContent.trim() === label
          );
          if (byLabel) return byLabel;
        }
        const doneWidget = host.querySelector('.jdgm-review-widget.jdgm--done-setup-widget');
        if (!doneWidget) return null;
        return doneWidget.querySelector('.jdgm-write-rev-link-dynamic, .jdgm-write-rev-link');
      }

      /* ---- Pinterest grid (layout mode 'grid') ---- */

      // Upgrade the CSS multi-column fallback to real column containers and
      // rebuild them whenever a breakpoint (phone/tablet/desktop) is crossed.
      setupGrid() {
        this.gridMq = window.matchMedia('(min-width: 990px)');
        this.gridMqTablet = window.matchMedia('(min-width: 750px)');
        this.onGridMqChange = () => this.buildGridColumns();
        this.gridMq.addEventListener('change', this.onGridMqChange);
        this.gridMqTablet.addEventListener('change', this.onGridMqChange);
        this.buildGridColumns();
        // Webfonts change line wraps and therefore card heights — once they
        // are in, deal again so the shortest-column placement works with the
        // final geometry. Resolves immediately if fonts are already loaded.
        document.fonts.ready.then(() => {
          if (this.isConnected && this.gridColumns) this.distributeGridCards();
        });
      }

      // Column count for the current viewport: phones 1, tablets 2,
      // desktop count is a section setting.
      gridColumnCount() {
        if (!this.gridMq.matches) return this.gridMqTablet.matches ? 2 : 1;
        const setting = parseInt(getComputedStyle(this).getPropertyValue('--ndog-testimonials-columns'), 10);
        return setting || 3;
      }

      // (Re)create the column containers and deal every card into them.
      buildGridColumns() {
        this.gridColumns = Array.from({ length: this.gridColumnCount() }, () => {
          const column = document.createElement('div');
          column.className = 'ndog-testimonials__column';
          return column;
        });
        this.track.innerHTML = '';
        this.gridColumns.forEach((column) => this.track.appendChild(column));
        this.track.classList.add('is-cols');
        this.distributeGridCards();
      }

      // Deal cards in this.allCards order, each into the currently shortest
      // column — true masonry packing, so a single very long review can't
      // unbalance the grid (decided 2026-07-27; reading order is height-
      // driven instead of strictly row-wise, accepted trade-off). Sole
      // exception: an active filter's summary card always leads the grid. Placement
      // stays reproducible: for the same cards, viewport and loaded fonts
      // the measured heights — and therefore the layout — are identical on
      // every deal. Heights only shift when the webfont arrives after the
      // first deal (setupGrid() re-deals on document.fonts.ready); card
      // photos are CSS-fixed at 220px and never shift anything.
      distributeGridCards() {
        // Start every deal from empty columns — on a re-deal the cards still
        // sit in their previous columns and would pollute the height
        // readings while the new arrangement is being built.
        this.allCards.forEach((card) => card.remove());
        // The active filter's summary card is pinned to the very first spot
        // (top of the leftmost column), regardless of heights — at most one
        // summary is ever visible (one per keyword filter, "All" shows none).
        const summary = this.allCards.find((card) => 'summary' in card.dataset && !card.hidden);
        if (summary) this.gridColumns[0].appendChild(summary);
        this.allCards.forEach((card) => {
          // The load-more CTA is pinned below the loop, not dealt.
          if ('jmCta' in card.dataset || card === summary) return;
          this.shortestGridColumn().appendChild(card);
        });
        const cta = this.allCards.find((card) => 'jmCta' in card.dataset);
        if (cta) this.gridColumns[this.gridColumns.length - 1].appendChild(cta);
      }

      // The column that is currently the shortest (hidden cards add no
      // height). Backbone of the masonry packing: the full deal AND the
      // appended load-more reviews both use it to fill the valleys.
      shortestGridColumn() {
        let shortest = this.gridColumns[0];
        this.gridColumns.forEach((column) => {
          if (column.offsetHeight < shortest.offsetHeight) shortest = column;
        });
        return shortest;
      }

      // Run a layout change and glide the track height from its old to its
      // new value (transition lives in CSS, gated behind reduced-motion).
      animateGridHeight(mutate) {
        const startHeight = this.track.offsetHeight;
        mutate();
        // A previous glide may still pin an explicit height — clear it first,
        // otherwise endHeight reads the frozen in-flight value, not the content.
        this.track.style.height = '';
        const endHeight = this.track.offsetHeight;
        if (startHeight === endHeight) return;
        this.track.classList.add('is-animating');
        this.track.style.height = `${startHeight}px`;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.track.style.height = `${endHeight}px`;
          });
        });
        clearTimeout(this.gridHeightTimer);
        this.gridHeightTimer = setTimeout(() => {
          this.track.style.height = '';
          this.track.classList.remove('is-animating');
        }, 350);
      }

      // Grid filtering: re-deal the cards inside a height glide. Cards show
      // instantly — a fade would leave their spots looking empty while it
      // runs; the enter fade stays exclusive to load-more, where new cards
      // only ever appear at the end.
      applyGridFilter(value) {
        this.animateGridHeight(() => {
          this.allCards.forEach((card) => this.setCardHidden(card, value));
          this.distributeGridCards();
        });
      }

      /* ---- Geometry helpers (recomputed live so they survive breakpoint
             changes and filter changes) ---- */

      // Cards currently shown (an active filter hides the rest via [hidden]).
      visibleCards() {
        return this.allCards.filter((card) => !card.hidden);
      }

      // Distance between two cards (card width + gap).
      cardStep() {
        const cards = this.visibleCards();
        if (cards.length < 2) return (cards[0] && cards[0].offsetWidth) || 1;
        return cards[1].offsetLeft - cards[0].offsetLeft || 1;
      }

      // How many cards fit in the viewport at once (≈3 desktop, 1 mobile).
      visibleCount() {
        return Math.max(1, Math.round(this.track.clientWidth / this.cardStep()));
      }

      maxScroll() {
        return this.track.scrollWidth - this.track.clientWidth;
      }

      pageCount() {
        return Math.max(1, Math.ceil(this.visibleCards().length / this.visibleCount()));
      }

      // Scroll position for page p, clamped so the last page lands at the end.
      pagePos(page) {
        const raw = page * this.visibleCount() * this.cardStep();
        return Math.min(raw, this.maxScroll());
      }

      // Page whose scroll position is closest to where we currently are.
      currentPage() {
        const left = this.track.scrollLeft;
        let best = 0;
        let bestDist = Infinity;
        for (let p = 0; p < this.pageCount(); p += 1) {
          const dist = Math.abs(left - this.pagePos(p));
          if (dist < bestDist) { bestDist = dist; best = p; }
        }
        return best;
      }

      /* ---- Dots ---- */

      // One dot per page; the whole row hides when there is nothing to page through.
      buildDots() {
        if (!this.dotsContainer) return;
        const count = this.pageCount();
        this.builtPageCount = count;
        this.dotsContainer.innerHTML = '';
        this.dotsContainer.style.display = count <= 1 ? 'none' : '';
        this.dots = Array.from({ length: count }, (_, page) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'ndog-testimonials__dot';
          dot.setAttribute('aria-label', String(page + 1));
          dot.addEventListener('click', () => this.goToPage(page));
          this.dotsContainer.appendChild(dot);
          return dot;
        });
      }

      /* ---- Filtering (keyword chips + star-bar rows, mutually exclusive) ---- */

      // Show only cards tagged with the clicked keyword; '' ("All") shows
      // everything. Also clears any active star filter (exclusive).
      applyFilter(value) {
        this.stopAllJmVideos();
        this.currentFilter = value;
        this.currentRating = 0;
        this.filterButtons.forEach((btn) => {
          const isActive = btn.dataset.filter === value;
          btn.classList.toggle('is-active', isActive);
          btn.setAttribute('aria-pressed', String(isActive));
        });
        this.syncRatingUi();
        this.refreshVisibleCards();
      }

      // Star filter (rating header): show only cards whose data-rating
      // matches; 0 clears. Activating it resets the keyword filter to "All".
      applyRatingFilter(rating) {
        this.stopAllJmVideos();
        this.currentRating = rating;
        this.currentFilter = '';
        this.filterButtons.forEach((btn) => {
          const isActive = btn.dataset.filter === '';
          btn.classList.toggle('is-active', isActive);
          btn.setAttribute('aria-pressed', String(isActive));
        });
        this.syncRatingUi();
        // Top-up starts BEFORE the visibility pass: its synchronous head
        // sets jmTopUp (and the loading label), so the very same pass hides
        // the matches AND the load-more CTA while the fetch runs — only the
        // loading note paints. The confirmed set is revealed in one batch
        // when the fetch settles (revealRatingCards). Match counting is
        // rating-based, not visibility-based, so the order is safe.
        if (rating > 0) this.ensureRatingCards(rating);
        this.refreshVisibleCards();
      }

      // Sync star-bar rows, chip and hint with the active star filter.
      syncRatingUi() {
        const rating = this.currentRating;
        this.ratingButtons.forEach((btn) => {
          const isActive = parseInt(btn.dataset.ratingFilter, 10) === rating;
          btn.classList.toggle('is-active', isActive);
          btn.setAttribute('aria-pressed', String(isActive));
        });
        if (this.ratingChip) {
          this.ratingChip.hidden = rating === 0;
          if (rating > 0) {
            // Separate singular template keeps the grammar right at 1 star.
            const template = rating === 1 && this.ratingChip.dataset.labelTemplateOne
              ? this.ratingChip.dataset.labelTemplateOne
              : this.ratingChip.dataset.labelTemplate || '';
            const labelEl = this.ratingChip.querySelector('[data-rating-chip-label]');
            if (labelEl) labelEl.textContent = template.replace('%rating%', String(rating));
          }
        }
        // The chip replaces the hint line while a star filter is active.
        if (this.ratingHint) this.ratingHint.hidden = rating !== 0;
      }

      // Re-apply the current filters to every card, rebuild the layout and
      // toggle the "nothing matches" note.
      refreshVisibleCards() {
        if (this.isGrid) {
          this.applyGridFilter(this.currentFilter);
        } else {
          this.allCards.forEach((card) => this.setCardHidden(card, this.currentFilter));
          this.track.scrollTo({ left: 0 });
          this.buildDots();
          this.update();
        }
        this.updateEmptyMessage();
      }

      // Show the empty note when an active star filter leaves no visible
      // review cards (summaries and the load-more CTA don't count).
      updateEmptyMessage() {
        if (!this.emptyMessage) return;
        this.emptyMessage.hidden = this.currentRating === 0 || this.visibleMatchCount() > 0;
      }

      // Visible review cards under the active filters — summaries and the
      // load-more CTA don't count.
      visibleMatchCount() {
        return this.allCards.filter(
          (card) => !card.hidden && !('summary' in card.dataset) && !('jmCta' in card.dataset)
        ).length;
      }

      // Visibility of one card under the given keyword filter value and the
      // active star filter (only one of the two is ever set). Judge.me cards
      // (incl. the load-more card) carry no keywords — the section setting
      // decides whether they ride along under active keyword filters.
      setCardHidden(card, value) {
        // Summary cards invert the rule: only visible while their own
        // keyword filter is active — "All" and the star filter show none.
        // Rendered first in the track, so a visible one is always first.
        if ('summary' in card.dataset) {
          card.hidden = value === '' || card.dataset.keyword !== value;
          return;
        }
        if (this.currentRating > 0) {
          // The load-more CTA stays while this rating still has unfetched
          // pages (fetching can add matches); a drained rating hides it,
          // and so does a running top-up fetch — a lone CTA in an
          // otherwise empty box reads as broken.
          if ('jmCta' in card.dataset) {
            card.hidden = this.jmTopUp || this.jmExhausted(this.currentRating);
            return;
          }
          // While the top-up fetch runs even loaded matches stay hidden —
          // the whole confirmed set then appears in one go (revealRatingCards).
          card.hidden = this.jmTopUp || parseInt(card.dataset.rating, 10) !== this.currentRating;
          return;
        }
        if (value === '') {
          // Default view: only the initial cap of the CURRENT viewport
          // hides anything here (see activeInitialCap).
          card.hidden = this.jmDeferred.has(card) && this.jmDeferred.get(card) >= this.activeInitialCap();
          return;
        }
        if ('jmCard' in card.dataset) {
          card.hidden = this.jmFilterMode !== 'every';
          return;
        }
        const keywords = (card.dataset.keywords || '').split('|').filter(Boolean);
        card.hidden = !keywords.includes(value);
      }

      /* ---- Judge.me load-more ---- */

      // "Load more" click. Under an active star filter the fetch is
      // server-filtered to that rating; unfiltered otherwise.
      async loadMoreReviews() {
        if (this.jmBusy || !this.jmTemplate || !this.jmCta) return;
        const rating = this.currentRating;
        if (this.jmExhausted(rating)) return;
        this.jmBusy = true;
        this.setMoreLoading(true);
        try {
          // Deferred initial cards (mobile cap) reveal first — nothing is
          // fetched until that stash is empty.
          let appended = this.revealDeferredCards(this.jmBatchLimit());
          if (appended === 0) appended = await this.fetchUntilAppended(rating);
          if (appended > 0 && !this.isGrid) {
            this.buildDots();
            this.update();
          }
          if (appended > 0) this.updateEmptyMessage();
          if (rating === 0 && this.jmExhausted(0) && !this.jmDeferredPending()) {
            this.removeJmCta();
          } else if (this.jmCta) {
            // A drained star filter hides the CTA until the filter changes.
            this.setCardHidden(this.jmCta, this.currentFilter);
          }
        } catch (error) {
          // Endpoint gone/unreachable — drop the CTA card, keep the slider alive.
          this.removeJmCta();
        } finally {
          this.jmBusy = false;
          this.setMoreLoading(false);
          // Same as in ensureRatingCards: a star filter activated while
          // this fetch ran was blocked by the jmBusy guard — top it up now
          // (plus a visibility pass in case nothing needs fetching).
          if (this.currentRating > 0 && this.currentRating !== rating) {
            this.ensureRatingCards(this.currentRating);
            this.refreshVisibleCards();
          }
        }
      }

      // Star filter activated: top up the visible matches to the configured
      // initial count (jm_max_reviews_* per viewport) with server-filtered
      // fetches — the bars show the GLOBAL distribution, so matching reviews
      // often just aren't fetched yet. Without a configured cap there is no
      // target count, so fetching only starts when nothing matches at all.
      // The empty-state card doubles as the loading indicator, but only
      // when the filter starts out empty — a top-up next to visible cards
      // runs silently (the new cards fade in anyway).
      async ensureRatingCards(rating) {
        if (!this.jmTemplate || this.jmBusy || this.jmExhausted(rating)) return;
        const cap = this.activeInitialCap();
        // Counted by data-rating, NOT by visibility — applyRatingFilter runs
        // this before its visibility pass (see there for why).
        const matches = this.allCards.filter(
          (card) => !('summary' in card.dataset) && !('jmCta' in card.dataset)
            && parseInt(card.dataset.rating, 10) === rating
        ).length;
        const needed = cap === Infinity ? (matches > 0 ? 0 : 1) : cap - matches;
        if (needed <= 0) return;
        this.jmBusy = true;
        this.jmTopUp = true;
        // Self-sync the CTA for callers without a following visibility pass
        // (the mid-flight re-trigger below); redundant but harmless when
        // applyRatingFilter refreshes right after.
        if (this.jmCta) this.setCardHidden(this.jmCta, this.currentFilter);
        this.setEmptyLoading(true);
        try {
          await this.fetchUntilAppended(rating, cap === Infinity ? 0 : needed);
        } catch (error) {
          // Endpoint unreachable — the reveal below still shows what is
          // already loaded, the CTA stays for a manual retry, nothing breaks.
        } finally {
          this.jmBusy = false;
          this.jmTopUp = false;
          this.setEmptyLoading(false);
          // Everything stayed hidden during the fetch — reveal the now-
          // confirmed set in ONE batch (only for the still-active filter).
          if (this.currentRating === rating) this.revealRatingCards();
          this.updateEmptyMessage();
          if (this.jmCta) this.setCardHidden(this.jmCta, this.currentFilter);
          // The user switched to another star filter while this fetch ran
          // (the jmBusy guard blocked that filter's own top-up) — run it
          // now; the extra visibility pass covers the case where that
          // top-up decides nothing needs fetching (cards must show then).
          if (this.currentRating > 0 && this.currentRating !== rating) {
            this.ensureRatingCards(this.currentRating);
            this.refreshVisibleCards();
          }
        }
      }

      // Un-hide every card of the active star filter in one simultaneous
      // batch (single fade, no stagger) — called once the top-up confirmed
      // what exists, so nothing trickles in afterwards. Grid mode re-deals
      // inside a height glide: while everything was hidden all columns
      // measured equal, so the cards appended mid-fetch clustered in the
      // first column.
      revealRatingCards() {
        const cards = this.allCards.filter(
          (card) => card.hidden && !('summary' in card.dataset) && !('jmCta' in card.dataset)
            && parseInt(card.dataset.rating, 10) === this.currentRating
        );
        if (cards.length === 0) return;
        cards.forEach((card) => {
          card.classList.add('ndog-testimonial-card--enter');
          card.style.transitionDelay = '0ms';
        });
        if (this.isGrid && this.gridColumns) {
          this.animateGridHeight(() => {
            cards.forEach((card) => { card.hidden = false; });
            this.distributeGridCards();
          });
        } else {
          cards.forEach((card) => { card.hidden = false; });
          this.buildDots();
          this.update();
        }
        this.animateCardsIn(cards);
      }

      // Swap the empty-state card between its loading and final label;
      // .is-loading adds the CSS ring spinner next to the loading text.
      setEmptyLoading(loading) {
        if (!this.emptyMessage) return;
        const label = loading
          ? this.emptyMessage.dataset.labelLoading
          : this.emptyMessage.dataset.labelEmpty;
        if (label) this.emptyMessage.textContent = label;
        this.emptyMessage.classList.toggle('is-loading', loading);
        if (loading) this.emptyMessage.hidden = false;
      }

      // Collect review cards beyond the initial caps in jmDeferred —
      // manual testimonials AND server-rendered JM reviews count, summary
      // cards don't. The first cards in DOM order stay (manual first,
      // then newest JM). Without a load-more CTA nothing could ever reveal
      // them, so the caps only arm while the CTA exists.
      // The map stores each card's position and fills for the SMALLER of
      // the two caps — visibility is decided live against the current
      // viewport's cap (setCardHidden/applyDeferredVisibility), so crossing
      // the breakpoint later still applies the right cap.
      deferInitialJmCards() {
        const minCap = Math.min(
          this.jmMobileInitial > 0 ? this.jmMobileInitial : Infinity,
          this.jmDesktopInitial > 0 ? this.jmDesktopInitial : Infinity
        );
        if (minCap === Infinity || !this.jmCta) return;
        const activeCap = this.activeInitialCap();
        this.allCards
          .filter((card) => !('summary' in card.dataset) && !('jmCta' in card.dataset))
          .forEach((card, position) => {
            if (position < minCap) return;
            this.jmDeferred.set(card, position);
            card.hidden = position >= activeCap;
          });
      }

      // Initial-visibility cap for the CURRENT viewport. Mobile with a value
      // of 0 falls back to the desktop cap ("same as desktop"); a cap of
      // 0/unset means no limit (Infinity).
      activeInitialCap() {
        let cap = this.jmDesktopInitial;
        if (this.mobileMq.matches && this.jmMobileInitial > 0) {
          cap = this.jmMobileInitial;
        }
        return cap > 0 ? cap : Infinity;
      }

      // Deferred cards whose reveal is still outstanding on the CURRENT
      // viewport — cards under the other viewport's smaller cap are already
      // visible here and don't count.
      jmDeferredPending() {
        const cap = this.activeInitialCap();
        for (const position of this.jmDeferred.values()) {
          if (position >= cap) return true;
        }
        return false;
      }

      // Breakpoint crossed without a reload (Customizer device toggle,
      // rotation): re-apply the deferred cards' visibility against the new
      // viewport's cap.
      applyDeferredVisibility() {
        if (this.jmDeferred.size === 0) return;
        // CTA already gone (e.g. drained under the other viewport's looser
        // cap) — nothing could ever reveal the pending cards here, so stop
        // deferring instead.
        if (!this.jmCta && this.jmDeferredPending()) this.jmDeferred.clear();
        this.refreshVisibleCards();
      }

      // Un-hide up to `limit` pending cards in their original order;
      // returns the count revealed. Only acts in the default view — active
      // filters already show deferred matches on their own (setCardHidden
      // ignores the deferred set outside the default view).
      revealDeferredCards(limit) {
        if (!this.jmDeferredPending()) return 0;
        if (this.currentRating > 0 || this.currentFilter !== '') return 0;
        const cap = this.activeInitialCap();
        // Cards deferred only for the other viewport's smaller cap are
        // already visible here — consume them too, so older cards can't end
        // up hidden behind newer ones after a breakpoint change.
        this.jmDeferred.forEach((position, card) => {
          if (position < cap) this.jmDeferred.delete(card);
        });
        const cards = this.allCards
          .filter((card) => this.jmDeferred.has(card))
          .slice(0, limit);
        cards.forEach((card) => this.jmDeferred.delete(card));
        if (this.isGrid && this.gridColumns && this.gridColumns.length > 1) {
          // Revealed cards sit mid-grid (dealt hidden, so they clustered) —
          // re-deal the columns inside a height glide instead of fading
          // (same reasoning as applyGridFilter).
          this.animateGridHeight(() => {
            cards.forEach((card) => { card.hidden = false; });
            this.distributeGridCards();
          });
        } else {
          cards.forEach((card, index) => {
            card.classList.add('ndog-testimonial-card--enter');
            card.style.transitionDelay = `${this.enterDelay(index)}ms`;
            card.hidden = false;
          });
          if (cards.length > 0) this.animateCardsIn(cards);
        }
        return cards.length;
      }

      // Cards appended per load-more call: capped by the mobile section
      // setting on phone viewports (<750px, the theme's mobile breakpoint),
      // a full endpoint page everywhere else.
      jmBatchLimit() {
        if (this.jmMobileBatch > 0 && this.mobileMq.matches) {
          return this.jmMobileBatch;
        }
        return Infinity;
      }

      // Append new cards to the given rating scope: drain the fetched-but-
      // unshown queue first, fetch further pages only when it runs dry
      // (pages can yield nothing: already shown or no text); returns the
      // appended count. Two modes: needed = 0 is the load-more click —
      // append up to one batch (jmBatchLimit), fetching only until SOMETHING
      // was appended. needed > 0 is the star-filter top-up — append exactly
      // that many, fetching until reached; surplus reviews stay queued for
      // the next load-more click. Aborts early when the user switches
      // filters mid-flight.
      async fetchUntilAppended(rating, needed = 0) {
        const limit = needed > 0 ? needed : this.jmBatchLimit();
        const goal = needed > 0 ? needed : 1;
        let appended = 0;
        do {
          const queue = this.jmQueues[rating] = this.jmQueues[rating] || [];
          if (queue.length === 0) {
            const page = (this.jmPages[rating] || 0) + 1;
            const data = await this.fetchJmPage(page, rating);
            this.jmPages[rating] = page;
            this.jmTotals[rating] = (data.pagination && data.pagination.total_pages) || page;
            queue.push(...(data.reviews || []));
          }
          appended += this.appendJmReviews(queue.splice(0, limit - appended));
        } while (appended < goal && !this.jmExhausted(rating) && this.currentRating === rating);
        return appended;
      }

      // True once every page of a rating scope (0 = unfiltered) was fetched
      // AND no fetched review is still queued for a later mobile batch.
      // Deferred initial cards are deliberately NOT counted here — the
      // fetch loop consults this and must terminate once pages run dry;
      // CTA lifetime handles them via jmDeferredPending() instead.
      jmExhausted(rating) {
        const queue = this.jmQueues && this.jmQueues[rating];
        if (queue && queue.length > 0) return false;
        const total = this.jmTotals[rating];
        return total != null && (this.jmPages[rating] || 0) >= total;
      }

      // GET one page of reviews from Judge.me's public widget endpoint —
      // optionally server-filtered to one star rating (filter_rating; the
      // param is undocumented but verified against the live endpoint).
      async fetchJmPage(page, rating) {
        const domain = encodeURIComponent(this.dataset.jmDomain || '');
        const productId = encodeURIComponent(this.dataset.jmProductId || '');
        const filter = rating > 0 ? `&filter_rating=${rating}` : '';
        const url = `https://judge.me/reviews/reviews_for_widget?url=${domain}&shop_domain=${domain}&platform=shopify&per_page=15&page=${page}&product_id=${productId}${filter}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Judge.me responded ${response.status}`);
        return response.json();
      }

      // Turn endpoint reviews into cards (before the CTA card); returns the count added.
      // Grid mode: new cards fill the shortest columns — already-placed cards
      // never move (the whole point of the JS columns); the CTA card hops to
      // the new end afterwards, and the whole batch lands inside a height
      // glide so the box grows smoothly instead of jumping to full height.
      appendJmReviews(reviews) {
        const newCards = [];
        reviews.forEach((review) => {
          if (!review.uuid || this.jmUuids.has(review.uuid)) return;
          const text = this.jmReviewText(review);
          if (!text) return;
          const card = this.buildJmCard(review, text);
          if (!this.jmTopUp) {
            // Load-more cards animate in as they arrive; top-up cards stay
            // hidden and animate later, all at once, in revealRatingCards.
            card.classList.add('ndog-testimonial-card--enter');
            card.style.transitionDelay = `${this.enterDelay(newCards.length)}ms`;
          }
          this.setCardHidden(card, this.currentFilter);
          this.jmUuids.add(review.uuid);
          newCards.push(card);
        });
        if (newCards.length === 0) return 0;
        const insert = () => {
          newCards.forEach((card) => {
            if (this.isGrid) {
              this.shortestGridColumn().appendChild(card);
            } else {
              this.track.insertBefore(card, this.jmCta);
            }
          });
          if (this.isGrid && this.jmCta) {
            // Pinned position: always last in the rightmost column.
            this.gridColumns[this.gridColumns.length - 1].appendChild(this.jmCta);
          }
        };
        if (this.isGrid && this.gridColumns) {
          this.animateGridHeight(insert);
        } else {
          insert();
        }
        // Keep this.allCards in logical (chronological) order — in grid
        // mode the DOM order is column-wise, so no re-query here.
        const ctaIndex = this.allCards.indexOf(this.jmCta);
        if (ctaIndex === -1) {
          this.allCards.push(...newCards);
        } else {
          this.allCards.splice(ctaIndex, 0, ...newCards);
        }
        if (!this.jmTopUp) this.animateCardsIn(newCards);
        return newCards.length;
      }

      // Stagger delay for the nth freshly appearing card: 40ms steps, capped
      // so big batches (desktop top-up can bring 14 at once) finish their
      // entrance together shortly after the first card instead of trickling
      // in for over a second.
      enterDelay(index) {
        return Math.min(index * 40, 240);
      }

      // Staggered fade-in for freshly loaded cards: the --enter class hides
      // them at insert; dropping it a frame later runs the CSS transition.
      animateCardsIn(cards) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            cards.forEach((card) => {
              card.classList.remove('ndog-testimonial-card--enter');
              card.addEventListener(
                'transitionend',
                () => { card.style.transitionDelay = ''; },
                { once: true }
              );
              // Reduced motion / no transition support: clean up regardless.
              setTimeout(() => { card.style.transitionDelay = ''; }, 800);
            });
          });
        });
      }

      // Plain text of one review — the endpoint only ships body_html.
      // '-' (also as "'-'", with quotes) is the placeholder for empty
      // review bodies (Judge.me rejects truly empty ones) — a body of only
      // quotes/dashes counts as no text, so appendJmReviews skips it like
      // any other empty one. Same rule as the Liquid loop.
      jmReviewText(review) {
        let text = '';
        if (review.body) {
          text = review.body.trim();
        } else if (review.body_html) {
          text = this.jmHtmlToText(review.body_html);
        }
        return /^['"‘’“”„\-–\s]*$/.test(text) ? '' : text;
      }

      // Flattens Judge.me HTML to plain text for CSS white-space: pre-line —
      // <br> becomes a line break, a closed <p> a blank line (paragraph gap),
      // so long reviews read as separate paragraphs instead of one block.
      jmHtmlToText(html) {
        const flat = html
          .replace(/\n/g, '')
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/p>/gi, '\n\n');
        const doc = new DOMParser().parseFromString(flat, 'text/html');
        return doc.body.textContent.replace(/\n{3,}/g, '\n\n').trim();
      }

      // Clone the Liquid <template> and fill it with one review's data.
      buildJmCard(review, text) {
        const card = this.jmTemplate.content.firstElementChild.cloneNode(true);
        card.dataset.uuid = review.uuid;
        card.dataset.rating = String(Math.round(review.rating));
        this.fillJmCardMedia(card, review);

        this.fillJmCardScore(card, Math.round(review.rating));

        // Review title sits inline after the score pill (one-line ellipsis).
        const titleEl = card.querySelector('.ndog-testimonial__header-title');
        if (titleEl) {
          if (review.title) {
            titleEl.textContent = review.title;
          } else {
            titleEl.remove();
          }
        }

        // Same word limit as the Liquid-rendered cards; the existing
        // delegated click handler picks up the cloned "read more" button.
        const words = text.split(/\s+/);
        const textEl = card.querySelector('.ndog-testimonial__text');
        if (this.jmMaxWords > 0 && words.length > this.jmMaxWords) {
          textEl.querySelector('.ndog-testimonial__text-short').textContent = `${words.slice(0, this.jmMaxWords).join(' ')}…`;
          textEl.querySelector('.ndog-testimonial__text-full').textContent = text;
        } else {
          textEl.textContent = text;
          const moreBtn = card.querySelector('[data-testimonial-more]');
          if (moreBtn) moreBtn.remove();
        }

        // Author sits in the card footer (bottom right); an empty name drops
        // the whole footer so no stray padding remains.
        const authorEl = card.querySelector('.ndog-testimonial__author');
        if (authorEl) {
          if (review.reviewer_name) {
            authorEl.textContent = review.reviewer_name;
          } else {
            const footer = authorEl.closest('.ndog-testimonial__footer');
            if (footer) footer.remove();
          }
        }

        this.fillJmCardReply(card, review);

        return card;
      }

      // Shop reply of a cloned card — mirrors the Liquid-rendered cards:
      // 1-3★ show it open, 4-5★ keep the collapsed <details> variant;
      // without a reply both template blocks are dropped.
      fillJmCardReply(card, review) {
        const open = card.querySelector('[data-jm-reply]');
        const folded = card.querySelector('[data-jm-reply-details]');
        const text = this.jmReplyText(review);
        const keep = !text ? null : Math.round(review.rating) <= 3 ? open : folded;
        [open, folded].forEach((el) => {
          if (!el) return;
          if (el !== keep) {
            el.remove();
            return;
          }
          const textEl = el.querySelector('[data-jm-reply-text]');
          if (textEl) textEl.textContent = text;
          el.hidden = false;
        });
      }

      // Plain text of a shop reply — same flattening as the review body.
      jmReplyText(review) {
        if (!review.reply_content) return '';
        return this.jmHtmlToText(review.reply_content);
      }

      // Score display of a cloned card. The template ships the section's
      // score_style variant: 'number' has a [data-jm-score] text span,
      // 'stars' a [data-jm-stars] row whose icons get .is-empty toggled.
      fillJmCardScore(card, rating) {
        const scoreEl = card.querySelector('[data-jm-score]');
        if (scoreEl) {
          scoreEl.textContent = `${rating}/5`;
          return;
        }
        const starsEl = card.querySelector('[data-jm-stars]');
        if (!starsEl) return;
        Array.from(starsEl.querySelectorAll('svg')).forEach((star, index) => {
          star.classList.toggle('is-empty', index + 1 > rating);
        });
        const template = starsEl.dataset.labelTemplate || '';
        if (template) starsEl.setAttribute('aria-label', template.replace('%rating%', String(rating)));
      }

      // Combined media list of one endpoint review: pictures first, then
      // YouTube videos (media_platform_hosted_video_infos, platform 'yt' —
      // the video id sits in media_platform_url). Vimeo/Cloudflare entries
      // are skipped on purpose, mirroring the Liquid-rendered cards.
      jmMediaList(review) {
        const fullUrl = (pic) => (pic.huge || pic.original || '').replace('width=1024', 'width=600');
        const pictures = (review.pictures_urls || []).map((pic) => ({
          full: fullUrl(pic),
          thumb: pic.small || pic.compact || fullUrl(pic),
        }));
        const videos = (review.media_platform_hosted_video_infos || [])
          .filter((info) => info && info.media_platform_name === 'yt')
          .map((info) => encodeURIComponent(String(info.media_platform_url || info.id || '')))
          .filter(Boolean)
          .map((videoId) => ({
            videoId,
            full: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
            thumb: `https://img.youtube.com/vi/${videoId}/default.jpg`,
          }));
        return pictures.concat(videos);
      }

      // Fill (or strip) the media parts of a cloned card: first medium as
      // main image (a video shows its YouTube thumbnail + play facade),
      // thumbs when there is more than one. Mirrors the Liquid markup for
      // server-rendered cards — those don't render the image wrapper at
      // all without media, so media-less clones drop it whole.
      fillJmCardMedia(card, review) {
        const main = card.querySelector('[data-jm-photo-main]');
        const media = this.jmShowPhotos ? this.jmMediaList(review) : [];

        if (media.length === 0 || !main) {
          const wrapper = card.querySelector('.ndog-testimonial__image-wrapper');
          if (wrapper) wrapper.remove();
          return;
        }

        card.classList.remove('ndog-testimonial-card--no-image');
        const first = media[0];
        main.src = first.full;
        const altTemplate = first.videoId ? main.dataset.videoAltTemplate : main.dataset.altTemplate;
        main.alt = (altTemplate || '').replace('%name%', review.reviewer_name || '');
        main.hidden = false;
        if (first.videoId) {
          const wrapper = card.querySelector('.ndog-testimonial__image-wrapper');
          const play = card.querySelector('[data-jm-play]');
          if (wrapper) wrapper.dataset.videoId = first.videoId;
          if (play) play.hidden = false;
        }

        const thumbsBox = card.querySelector('.ndog-testimonial__thumbs');
        if (media.length < 2) {
          if (thumbsBox) thumbsBox.remove();
          return;
        }

        const skeleton = thumbsBox.querySelector('[data-jm-thumb]');
        media.slice(0, 5).forEach((medium, index) => {
          const thumb = skeleton.cloneNode(true);
          thumb.dataset.full = medium.full;
          thumb.classList.toggle('is-active', index === 0);
          const labelTemplate = medium.videoId
            ? skeleton.dataset.videoLabelTemplate
            : skeleton.dataset.labelTemplate;
          thumb.setAttribute('aria-label', (labelTemplate || '').replace('%index%', String(index + 1)));
          if (medium.videoId) {
            thumb.dataset.videoId = medium.videoId;
            thumb.classList.add('ndog-testimonial__thumb--video');
          }
          thumb.querySelector('img').src = medium.thumb;
          thumbsBox.appendChild(thumb);
        });
        skeleton.remove();
        thumbsBox.hidden = false;
      }

      /* ---- YouTube facade (photo/video cards) ---- */

      // Swap a card's facade for the real embed — the (cookieless) YouTube
      // iframe only exists after this explicit user click.
      playJmVideo(button) {
        const wrapper = button.closest('.ndog-testimonial__image-wrapper');
        const host = wrapper && wrapper.querySelector('[data-jm-video-host]');
        const videoId = wrapper && wrapper.dataset.videoId;
        if (!host || !videoId) return;
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0`;
        iframe.title = button.getAttribute('aria-label') || '';
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
        host.replaceChildren(iframe);
        host.hidden = false;
        button.hidden = true;
      }

      // Tear down a card's playing embed (if any); the play button returns
      // whenever the card's main slot still shows a video thumbnail.
      stopJmVideo(wrapper) {
        const host = wrapper.querySelector('[data-jm-video-host]');
        if (!host || host.hidden) return;
        host.replaceChildren();
        host.hidden = true;
        const play = wrapper.querySelector('[data-jm-play]');
        if (play && wrapper.dataset.videoId) play.hidden = false;
      }

      // Stop every playing review video — called on slider navigation and
      // filter changes so no audio keeps playing from an off-screen card.
      stopAllJmVideos() {
        this.querySelectorAll('.ndog-testimonial__image-wrapper').forEach((wrapper) => {
          this.stopJmVideo(wrapper);
        });
      }

      // Drop the load-more card once every page is loaded (or the endpoint
      // failed) — faded out first so it doesn't just blink away.
      removeJmCta() {
        if (!this.jmCta) return;
        const cta = this.jmCta;
        this.jmCta = null;
        cta.classList.add('ndog-testimonial-card--enter');
        setTimeout(() => {
          cta.remove();
          this.allCards = this.allCards.filter((card) => card !== cta);
          if (this.isGrid) return;
          this.buildDots();
          this.update();
        }, 380);
      }

      // Toggle the CTA button between its idle and loading label.
      setMoreLoading(loading) {
        const button = this.jmCta && this.jmCta.querySelector('[data-jm-more]');
        if (!button) return;
        button.disabled = loading;
        const label = button.querySelector('.ndog-review-cta__label');
        if (label) label.textContent = loading ? button.dataset.labelLoading : button.dataset.labelIdle;
      }

      /* ---- Events & navigation ---- */

      bindEvents() {
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

        // Load-more click, delegated so it survives DOM changes around the CTA card.
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-jm-more]')) this.loadMoreReviews();
        });

        // Media thumb click: swap the card's main image (per-card mini
        // slideshow). A video thumb swaps in its YouTube facade (thumbnail +
        // play button); any running embed is torn down first.
        this.addEventListener('click', (event) => {
          const thumb = event.target.closest('[data-jm-thumb]');
          if (!thumb || !thumb.dataset.full) return;
          const wrapper = thumb.closest('.ndog-testimonial__image-wrapper');
          const main = wrapper && wrapper.querySelector('[data-jm-photo-main]');
          if (!main) return;
          this.stopJmVideo(wrapper);
          main.src = thumb.dataset.full;
          const play = wrapper.querySelector('[data-jm-play]');
          if (thumb.dataset.videoId) {
            wrapper.dataset.videoId = thumb.dataset.videoId;
            if (play) play.hidden = false;
          } else {
            delete wrapper.dataset.videoId;
            if (play) play.hidden = true;
          }
          wrapper.querySelectorAll('[data-jm-thumb]').forEach((button) => {
            button.classList.toggle('is-active', button === thumb);
          });
        });

        // Video play click: replace the YouTube facade with the real embed.
        this.addEventListener('click', (event) => {
          const play = event.target.closest('[data-jm-play]');
          if (play) this.playJmVideo(play);
        });

        // Expand/collapse truncated testimonial text via the "read more" button.
        // Attached to the component itself, so it is disposed together with it.
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-testimonial-more]');
          if (!button) return;
          const card = button.closest('.ndog-testimonial-card');
          if (!card) return;
          const expanded = card.classList.toggle('is-expanded');
          button.setAttribute('aria-expanded', String(expanded));
          button.textContent = expanded ? button.dataset.labelLess : button.dataset.labelMore;
        });

        this.filterButtons.forEach((btn) => {
          btn.addEventListener('click', () => this.applyFilter(btn.dataset.filter));
        });

        // Star-bar rows toggle the rating filter; the chip clears it.
        this.ratingButtons.forEach((btn) => {
          btn.addEventListener('click', () => {
            const rating = parseInt(btn.dataset.ratingFilter, 10) || 0;
            this.applyRatingFilter(rating === this.currentRating ? 0 : rating);
          });
        });
        if (this.ratingChip) {
          this.ratingChip.addEventListener('click', () => this.applyRatingFilter(0));
        }

        // Theme editor only: selecting a summary block switches to its
        // filter, otherwise the editor would stare at a hidden element.
        if (window.Shopify && window.Shopify.designMode) {
          this.addEventListener('shopify:block:select', (event) => {
            const summary = event.target.closest('[data-summary]');
            if (summary) this.applyFilter(summary.dataset.keyword);
          });
        }

        // Slider-only geometry tracking — grid mode never scrolls or pages.
        if (this.isGrid) return;

        this.onScroll = () => {
          if (this.raf) return;
          this.raf = requestAnimationFrame(() => { this.raf = null; this.update(); });
        };
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        this.onResize = () => {
          if (this.pageCount() !== this.builtPageCount) this.buildDots();
          this.update();
        };
        window.addEventListener('resize', this.onResize);
      }

      step(direction) {
        this.goToPage(this.currentPage() + direction);
      }

      goToPage(page) {
        this.stopAllJmVideos();
        const clamped = Math.max(0, Math.min(page, this.pageCount() - 1));
        this.track.scrollTo({ left: this.pagePos(clamped), behavior: 'smooth' });
      }

      // Sync active dot + arrow visibility with the current scroll position.
      update() {
        const page = this.currentPage();
        if (this.dots) {
          this.dots.forEach((d, i) => d.classList.toggle('is-active', i === page));
        }
        const atStart = this.track.scrollLeft <= 1;
        const atEnd = this.track.scrollLeft >= this.maxScroll() - 1;
        if (this.prevBtn) this.prevBtn.hidden = atStart;
        if (this.nextBtn) this.nextBtn.hidden = atEnd;
      }
    }
  );
}
